import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Controller, useForm, UseFormMethods} from 'react-hook-form';
import {FormField} from '@components/system/FormField/FormField';
import {IProcedure} from '@shared/services/service-mappers/SmartCoderServiceMapper';
import {
  GENERATE_CPT_CODE_TAB_ID,
  SMART_CODER_PROCEDURE_TYPES
} from '@shared/constants/Mapping';
import {getAllProcedures} from '@shared/services/SmartCoderServices';
import Loader from '@components/framework/loader/Loader';
import './ProcessingTimers.scss';
import {
  GUIDELINES,
  PAYERS_OPTIONS,
  PLANS_OPTIONS,
  PROCESSING_TIMES
} from './processingTimesByProcedure';

interface IProcessingTimes {
  clinicGroupId: number;
  clinicId: number;
  useFormMethods: UseFormMethods;
  handleChangeTabToProcedure?: (v: string) => void;
}

const ProcessingTimes = (props: IProcessingTimes): JSX.Element => {
  const {
    clinicGroupId,
    clinicId,
    useFormMethods,
    handleChangeTabToProcedure
  } = props;

  const {t} = useTranslation();
  const {procedure_code_id: procedureId} = useFormMethods.watch([
    'procedure_code_id'
  ]);

  const {control, errors, setValue, watch} = useForm();

  const [procedures, setProcedures] = useState({
    isLoading: false,
    data: [] as IProcedure[]
  });

  useEffect(() => {
    const fetchProcedures = async () => {
      setProcedures(p => ({
        ...p,
        isLoading: true
      }));
      try {
        const procedureRes = await getAllProcedures({
          clinicGroupId,
          clinicId,
          params: {
            type: SMART_CODER_PROCEDURE_TYPES.PAIN_MED_PROCEDURE
          }
        });
        if (Array.isArray(procedureRes.data)) {
          setProcedures({
            data: procedureRes.data,
            isLoading: false
          });
        } else {
          setProcedures({
            data: [],
            isLoading: false
          });
        }
      } catch (err) {
        setProcedures({
          data: [],
          isLoading: false
        });
      }
    };
    fetchProcedures();
  }, [clinicGroupId, clinicId]);

  const selectedProcedure = procedures.data.find(p => p.id == procedureId);

  const selectedPayerId = watch('payer_id');
  const selectedPlanId = watch('plan_id');

  const {processingTime, guidelines} = useMemo(() => {
    if (!selectedPayerId || !selectedPlanId || !selectedProcedure) {
      return {};
    }

    let processingTimeVal =
      PROCESSING_TIMES[selectedProcedure.label]?.[selectedPayerId]?.[
        selectedPlanId
      ];
    const guidelinesVal = GUIDELINES.find(
      g =>
        g.payerId === selectedPayerId &&
        g.procedureName === selectedProcedure.label
    );

    if (!processingTimeVal) {
      processingTimeVal = 'Not Available';
    }

    return {processingTime: processingTimeVal, guidelines: guidelinesVal};
  }, [selectedPayerId, selectedPlanId, selectedProcedure]);

  if (!procedureId) {
    return (
      <div className="processing-times-wrapper">
        <p>
          {t(
            'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SELECT_PROCEDURE_TO_VIEW_PROCESSING_TIMES_HELPER_TEXT'
          )}
          <span
            className="link-style"
            onClick={() =>
              handleChangeTabToProcedure(GENERATE_CPT_CODE_TAB_ID.PROCEDURE)
            }>
            {t(
              'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.GO_TO_PROCEDURE_TAB_LINK_TEXT'
            )}
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className="processing-times-wrapper">
      {procedures.isLoading && <Loader />}
      {!procedures.isLoading && (
        <>
          <div className="procedure-field-wrapper">
            <div className="procedure-label">
              {t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.PROCEDURE_FIELD_LABEL')}
              :
            </div>
            <div className="procedure-value">{selectedProcedure?.label}</div>
          </div>
          <div className="payer-field-wrapper">
            <Controller
              control={control}
              name="payer_id"
              rules={{
                validate: value => {
                  if (!value) {
                    return t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VALIDATION_ERROR_FIELD_REQUIRED',
                      {
                        field: t(
                          'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PAYER_FIELD_LABEL'
                        ) as string
                      }
                    ) as string;
                  }
                  return true;
                }
              }}
              render={inputProps => {
                return (
                  <FormField
                    label={t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PAYER_FIELD_LABEL'
                    )}
                    type="select"
                    placeholder={t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PAYER_FIELD_PLACEHOLDER'
                    )}
                    itemList={PAYERS_OPTIONS.map(payer => ({
                      label: payer.label,
                      value: payer.id
                    }))}
                    required={true}
                    {...inputProps}
                    error={errors['payer_id']}
                    onChange={e => {
                      setValue('plan_id', null);
                      inputProps.onChange(e);
                    }}
                  />
                );
              }}
            />
          </div>

          <div className="plan-field-wrapper">
            <Controller
              control={control}
              name="plan_id"
              rules={{
                validate: value => {
                  if (!value) {
                    return t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VALIDATION_ERROR_FIELD_REQUIRED',
                      {
                        field: t(
                          'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PLAN_FIELD_LABEL'
                        ) as string
                      }
                    ) as string;
                  }
                  return true;
                }
              }}
              render={inputProps => {
                return (
                  <FormField
                    label={t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PLAN_FIELD_LABEL'
                    )}
                    type="select"
                    placeholder={t(
                      'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PLAN_FIELD_PLACEHOLDER'
                    )}
                    itemList={PLANS_OPTIONS.filter(
                      plan => plan.payerId === selectedPayerId
                    ).map(plan => ({
                      label: plan.label,
                      value: String(plan.id)
                    }))}
                    required={true}
                    disabled={!selectedPayerId}
                    {...inputProps}
                    error={errors['plan_id']}
                  />
                );
              }}
            />
          </div>

          <div className="processing-time-wrapper">
            {!selectedPayerId && !selectedPlanId && (
              <p>
                {t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SELECT_PAYER_AND_PLAN_MESSAGE'
                )}
              </p>
            )}
            {selectedPayerId && !selectedPlanId && (
              <p>
                {t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SELECT_PLAN_MESSAGE'
                )}
              </p>
            )}
            {processingTime && (
              <p className="processing-time-message">
                {t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PROCESSING_TIME_LABEL'
                )}
                :{' '}
                <span className="processing-time-value">
                  {processingTime}
                </span>
              </p>
            )}

            {guidelines && (
              <div className="guidelines-section">
                <h3 className="guidelines-title">Guidelines</h3>
                <div className="guidelines-container">
                  {guidelines.initial && guidelines.initial.length > 0 && (
                    <div className="guidelines-block">
                      <h4 className="guidelines-subtitle">Initial:</h4>
                      <ul className="guidelines-list">
                        {guidelines.initial.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {guidelines.repeat && guidelines.repeat.length > 0 && (
                    <div className="guidelines-block">
                      <h4 className="guidelines-subtitle">Repeat:</h4>
                      <ul className="guidelines-list">
                        {guidelines.repeat.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {guidelines.limitations && guidelines.limitations.length > 0 && (
                    <div className="guidelines-block">
                      <h4 className="guidelines-subtitle">Limitations:</h4>
                      <ul className="guidelines-list">
                        {guidelines.limitations.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProcessingTimes;
